import React, { useState, useEffect } from 'react';
import { makeClient } from './scheduleClient';
import { Plus, Pencil, Trash2, User, Mail, Phone, Crown, KeyRound, CloudOff, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/schedule`;
const ACCOUNT_API = `${process.env.REACT_APP_BACKEND_URL}/api/account`;
const axios = makeClient();

const EMPTY = { name: '', email: '', phone: '', active: true, is_admin: false };

const EmployeeManager = () => {
  const [employees, setEmployees] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [loginFor, setLoginFor] = useState(null);
  const [loginMode, setLoginMode] = useState('create');
  const [loginPassword, setLoginPassword] = useState('');

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API}/employees`);
      setEmployees(res.data);
    } catch (e) {
      toast.error('Failed to load employees');
    }
  };
  useEffect(() => { fetchEmployees(); }, []);

  const openDialog = (employee = null) => {
    setEditing(employee);
    setForm(employee
      ? { name: employee.name, email: employee.email || '', phone: employee.phone || '', active: employee.active !== false, is_admin: !!employee.is_admin }
      : EMPTY);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        await axios.put(`${API}/employees/${editing.id}`, form);
        toast.success('Employee updated');
      } else {
        await axios.post(`${API}/employees`, form);
        toast.success('Employee added');
      }
      setDialogOpen(false);
      fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save employee');
    } finally { setLoading(false); }
  };

  const handleDelete = async (employee) => {
    const msg = employee.linked
      ? `Delete ${employee.name}? Their cloud login is removed too, along with their venue roles and blackout dates.`
      : `Delete ${employee.name}? Their venue roles and blackout dates are removed too.`;
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API}/employees/${employee.id}`);
      toast.success('Employee deleted');
      fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete employee');
    }
  };

  const handleCreateLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (loginMode === 'reset') {
        const res = await axios.put(`${ACCOUNT_API}/hosts/${loginFor.user_id}/reset-password`, { password: loginPassword });
        toast.success(res.data?.message || 'Password reset');
      } else {
        await axios.post(`${ACCOUNT_API}/hosts`, { email: loginFor.email, name: loginFor.name, password: loginPassword });
        toast.success(`Cloud login created for ${loginFor.name}`);
      }
      setLoginFor(null);
      setLoginPassword('');
      fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6">
      <Card data-testid="employee-manager">
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-2xl flex items-center space-x-2">
                <User className="h-6 w-6 text-[#fbdd68]" />
                <span>Employee Management</span>
              </CardTitle>
              <CardDescription>
                Add, edit, or remove employees. Everyone here syncs with your BIG Hat program.
                An employee whose email matches a cloud login is linked automatically.
              </CardDescription>
            </div>
            <Button onClick={() => openDialog()} data-testid="add-employee-btn"
              className="bg-[#fbdd68] text-[#000e2a] font-bold hover:bg-[#ffe685]">
              <Plus className="h-4 w-4 mr-2" /> Add Employee
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {employees.map((emp) => (
              <Card key={emp.id} className="border border-white/10 bg-white/5 hover:border-[#fbdd68]/40 transition-colors"
                data-testid={`employee-card-${emp.id}`}>
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-lg text-white truncate">{emp.name}</h3>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {emp.is_owner && (
                            <Badge className="bg-[#fbdd68] text-[#000e2a]"><Crown className="h-3 w-3 mr-1" />Owner</Badge>
                          )}
                          {emp.is_admin && (
                            <Badge className="bg-blue-600/80 text-white"><Shield className="h-3 w-3 mr-1" />Admin</Badge>
                          )}
                          {emp.linked && !emp.is_owner && (
                            <Badge className="bg-emerald-600/80 text-white">Cloud login</Badge>
                          )}
                          {!emp.linked && (
                            <Badge variant="outline" className="border-white/20 text-slate-400">
                              <CloudOff className="h-3 w-3 mr-1" />No login yet
                            </Badge>
                          )}
                          {emp.active === false && (
                            <Badge variant="outline" className="border-red-400/40 text-red-400">Inactive</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex space-x-1">
                        <Button variant="ghost" size="icon" onClick={() => openDialog(emp)}
                          className="h-8 w-8 hover:bg-[#fbdd68]/10" title="Edit employee"
                          data-testid={`edit-employee-${emp.id}`}>
                          <Pencil className="h-4 w-4 text-[#fbdd68]" />
                        </Button>
                        {!emp.linked && emp.email && (
                          <Button variant="ghost" size="icon" onClick={() => { setLoginFor(emp); setLoginMode('create'); setLoginPassword(''); }}
                            className="h-8 w-8 hover:bg-emerald-500/10" title="Create cloud login"
                            data-testid={`create-login-${emp.id}`}>
                            <KeyRound className="h-4 w-4 text-emerald-400" />
                          </Button>
                        )}
                        {emp.linked && !emp.is_owner && emp.login_role !== 'master_admin' && (
                          <Button variant="ghost" size="icon" onClick={() => { setLoginFor(emp); setLoginMode('reset'); setLoginPassword(''); }}
                            className="h-8 w-8 hover:bg-amber-500/10" title="Reset password"
                            data-testid={`reset-password-${emp.id}`}>
                            <KeyRound className="h-4 w-4 text-amber-400" />
                          </Button>
                        )}
                        {!emp.is_owner && emp.login_role !== 'master_admin' && (
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(emp)}
                            className="h-8 w-8 hover:bg-red-500/10" title="Delete employee"
                            data-testid={`delete-employee-${emp.id}`}>
                            <Trash2 className="h-4 w-4 text-red-400" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2 text-sm text-slate-400">
                      <div className="flex items-center space-x-2">
                        <Mail className="h-4 w-4 shrink-0" />
                        <span className="truncate">{emp.email || 'No email'}</span>
                      </div>
                      {emp.phone && (
                        <div className="flex items-center space-x-2">
                          <Phone className="h-4 w-4 shrink-0" />
                          <span>{emp.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {employees.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <User className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No employees added yet</p>
            </div>
          )}
          <p className="text-xs text-slate-500 mt-4">
            This is the same list as the <span className="text-[#fbdd68]">Hosts</span> tab of the dashboard.
            Deleting someone with a cloud login also removes that login.
          </p>
        </CardContent>
      </Card>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]" data-testid="employee-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Update employee information' : 'Enter the new employee details'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="emp-name">Full Name *</Label>
                <Input id="emp-name" value={form.name} required placeholder="John Doe"
                  onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="employee-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emp-email">Email</Label>
                <Input id="emp-email" type="email" value={form.email} placeholder="john@example.com"
                  onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="employee-email-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emp-phone">Phone (Optional)</Label>
                <Input id="emp-phone" type="tel" value={form.phone} placeholder="555-0100"
                  onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="employee-phone-input" />
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="emp-active" checked={form.active}
                  onCheckedChange={(v) => setForm({ ...form, active: !!v })} data-testid="employee-active-checkbox" />
                <Label htmlFor="emp-active">Active (available for the schedule)</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="emp-admin" checked={form.is_admin}
                  onCheckedChange={(v) => setForm({ ...form, is_admin: !!v })} data-testid="employee-admin-checkbox" />
                <div>
                  <Label htmlFor="emp-admin">Admin</Label>
                  <p className="text-xs text-slate-500">Can manage the schedule: employees, venues, events and reports</p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={loading} data-testid="employee-save-btn"
                className="bg-[#fbdd68] text-[#000e2a] font-bold hover:bg-[#ffe685]">
                {loading ? 'Saving…' : editing ? 'Save Changes' : 'Add Employee'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create cloud login dialog */}
      <Dialog open={!!loginFor} onOpenChange={(o) => !o && setLoginFor(null)}>
        <DialogContent className="sm:max-w-[460px]" data-testid="create-login-dialog">
          <DialogHeader>
            <DialogTitle>{loginMode === 'reset' ? 'Reset password' : 'Create cloud login'}</DialogTitle>
            <DialogDescription>
              {loginMode === 'reset'
                ? `Set a temporary password for ${loginFor?.name}. They will be required to choose a new one the next time they sign in.`
                : `Give ${loginFor?.name} a password so they can sign in with ${loginFor?.email} and claim events.`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateLogin}>
            <div className="space-y-2 py-2">
              <Label htmlFor="login-password">Password</Label>
              <Input id="login-password" type="password" value={loginPassword} required minLength={12}
                placeholder="At least 12 characters"
                onChange={(e) => setLoginPassword(e.target.value)} data-testid="create-login-password-input" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={loading || !loginPassword} data-testid="create-login-save-btn"
                className="bg-[#fbdd68] text-[#000e2a] font-bold hover:bg-[#ffe685]">
                {loading ? 'Saving…' : loginMode === 'reset' ? 'Reset password' : 'Create login'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeManager;
